import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { captureMessage } from '@tink/core/src/sentry';
import { UNEXPECTED_ERROR } from '../components/ApplicationErrorBoundary/ApplicationErrorBoundary';
import { setApplicationError } from '../stores/app-state-store';
import { AppThunk } from '../stores/redux-store';
import { ApplicationError } from '../components/ApplicationErrorBoundary/errors/ApplicationError';
import {
  AuthorizePayload,
  describeClientApp,
  DescribeRequest,
  OauthDescribeErrorResponse,
  OauthDescribeSuccessResponse,
  OauthErrorCode,
} from '../lib-api/services/Oauth';
import { base, isResponseOk, ResponseObj } from '../lib-api/services/base';
import {
  followingEmbeddedRule,
  postNotificationHandler,
} from '../utilities/post-notification-handler';
import { getOauthDescriptionSuccess } from '../stores/oauth-store';
import {
  BundledFlowParameters,
  describe as reportsDescribe,
} from '../reports/services/reports-client';
import { fetchReportsDescriptionSuccess } from '../stores/reports-store';
import { ERR_REPORTS_FAILED_DESCRIBE } from '../reports/services/reports';
import { ReportDescriptionResponse } from '../reports/models/report';

const EMBEDDED_NOT_ALLOWED_ERROR = {
  errorStatus: ErrorStatus.BAD_REQUEST,
  errorReason: ErrorReason.INVALID_STATE_EMBED_NOT_ALLOWED,
};

export const OAUTH_DESCRIBE_FAILED_ERROR = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_OAUTH_DESCRIBE,
};

export const ERR_OAUTH_INVALID_REDIRECT_URI = {
  errorStatus: ErrorStatus.BAD_REQUEST,
  errorReason: ErrorReason.INVALID_STATE_REDIRECT_URI,
};

export const ERR_OAUTH_INVALID_SCOPE = {
  errorStatus: ErrorStatus.BAD_REQUEST,
  errorReason: ErrorReason.INVALID_STATE_SCOPE,
};

type FetchOauthDescriptionPayload = BundledFlowParameters & AuthorizePayload;

interface ClientDescriptionService {
  describeClient: (payload: DescribeRequest) => Promise<{
    oauthDescription: OauthDescribeSuccessResponse;
  }>;
  describeReportsClient: (payload: FetchOauthDescriptionPayload) => Promise<{
    oauthDescription: OauthDescribeSuccessResponse;
    reportDescription?: ReportDescriptionResponse;
  }>;
}

export type DescribeClient = {
  fetchOauthDescription: (
    payload: DescribeRequest
  ) => Promise<ResponseObj<OauthDescribeSuccessResponse> | ResponseObj<OauthDescribeErrorResponse>>;
  fetchReportsDescription: (
    payload: BundledFlowParameters
  ) => Promise<ResponseObj<ReportDescriptionResponse>>;
};

export const createDescriptionService = (client: DescribeClient): ClientDescriptionService => {
  return {
    async describeClient(payload): Promise<{ oauthDescription: OauthDescribeSuccessResponse }> {
      const response = await client.fetchOauthDescription(payload);

      if (isResponseOk<OauthDescribeSuccessResponse, OauthDescribeErrorResponse>(response)) {
        const oauthDescription = response.data;

        if (!followingEmbeddedRule(oauthDescription)) {
          throw new ApplicationError(EMBEDDED_NOT_ALLOWED_ERROR);
        }

        return { oauthDescription };
      }

      if (response.data.errorCode === OauthErrorCode.INVALID_REDIRECT_URI) {
        throw new ApplicationError(ERR_OAUTH_INVALID_REDIRECT_URI);
      }

      if (response.data.errorCode === OauthErrorCode.INVALID_SCOPE) {
        throw new ApplicationError(ERR_OAUTH_INVALID_SCOPE);
      }

      throw new ApplicationError(OAUTH_DESCRIBE_FAILED_ERROR);
    },
    async describeReportsClient({
      reportTypes,
      refreshableItems,
      accountDialogType,
      clientId,
      redirectUri,
      state,
    }) {
      let reportDescription: ReportDescriptionResponse | undefined;

      const oauthDescribePayload: DescribeRequest = {
        clientId,
        redirectUri,
        state,
      };

      const res = await client.fetchReportsDescription({
        reportTypes,
        refreshableItems,
        accountDialogType,
      });

      if (res.isOk()) {
        reportDescription = res.data;

        if (
          res.headers &&
          typeof res.headers.getContentType === 'function' &&
          res.headers.getContentType() === 'text/html'
        ) {
          captureMessage('Invalid scopes configured');
        }

        oauthDescribePayload.refreshItems = reportDescription.refreshItems;
      } else {
        throw new ApplicationError(ERR_REPORTS_FAILED_DESCRIBE);
      }

      const { oauthDescription } = await this.describeClient(oauthDescribePayload);

      return {
        reportDescription,
        oauthDescription,
      };
    },
  };
};

const describeClient = {
  fetchOauthDescription: describeClientApp,
  fetchReportsDescription: reportsDescribe,
};

export const describeService = createDescriptionService(describeClient);

export const describeWithDispatch = (payload: DescribeRequest): AppThunk => {
  return async (dispatch) => {
    try {
      const res = await describeService.describeClient(payload);

      base.setClientName(res.oauthDescription.clientName);
      postNotificationHandler.setOauthDescription(res.oauthDescription);

      dispatch(getOauthDescriptionSuccess(res.oauthDescription));
    } catch (e) {
      if (e instanceof ApplicationError) {
        dispatch(setApplicationError(e.commonError));
      } else {
        dispatch(setApplicationError(UNEXPECTED_ERROR));
      }
    }
  };
};

export const describeReportsWithDispatch = (payload: FetchOauthDescriptionPayload): AppThunk => {
  return async (dispatch) => {
    try {
      const res = await describeService.describeReportsClient(payload);

      base.setClientName(res.oauthDescription.clientName);
      postNotificationHandler.setOauthDescription(res.oauthDescription);

      dispatch(getOauthDescriptionSuccess(res.oauthDescription));

      if (res.reportDescription) {
        dispatch(fetchReportsDescriptionSuccess(res.reportDescription));
      }
    } catch (e) {
      if (e instanceof ApplicationError) {
        dispatch(setApplicationError(e.commonError));
      } else {
        dispatch(setApplicationError(UNEXPECTED_ERROR));
      }
    }
  };
};
